import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { shutdown } from '@intercom/messenger-js-sdk';
import Loading from 'src/components/common/Loading';
import queryClient from 'src/queryClient';
import * as appOperations from 'src/store/modules/app/operations';
import { authOperations } from 'src/store/modules/auth';
import { trackLogOut } from 'src/utils/mixpanel';
import { amplifySignOut } from 'src/utils/auth';
import { clearDivisions } from 'src/utils/divisionStorage';
import { getSsoReferrer, getApiHost, clearAuthFlowData } from 'src/utils/ssoReferrer';

/**
 * Single SignOut component used by both V1 and V2 sessions.
 *
 * Always navigates to /api/auth/v2/logout after local cleanup. The V2 endpoint
 * is [AllowAnonymous] and clears legacy V1 cookies (x-sl-user, x-sl-session)
 * via AuthenticationHelper.RemoveAuthCookies in addition to sl-auth-token,
 * then redirects through Cognito /logout to clear the hosted UI session.
 *
 * For V1 sessions, the only thing the V2 endpoint cannot do server-side is
 * Cognito GlobalSignOut (it requires a bearer token). The realistic exposure
 * of that gap — a Cognito refresh token outliving the cookie clearance — is
 * closed by amplifySignOut() below, which clears Amplify's localStorage cache
 * including refresh tokens.
 */
const SignOut = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // ?referrer query param overrides the host-derived one. Required for the
    // advisor logout flow which lands on enterprise-sso/sign_out?referrer=
    // advisor-dev — without this getSsoReferrer() resolves to 'enterprise-sso'.
    const queryReferrer = new URLSearchParams(window.location.search).get('referrer');
    const referrer = queryReferrer || getSsoReferrer();
    const redirectUrl = `https://${getApiHost()}/api/auth/v2/logout?referrer=${encodeURIComponent(referrer)}`;

    const performLogout = async () => {
      try {
        await amplifySignOut();
      } catch (e) {
        console.warn('SignOut: amplifySignOut failed:', e);
      }

      try {
        shutdown();
        trackLogOut();
        dispatch(authOperations.signOutSuccess());
        dispatch(appOperations.clearReduxState());
        queryClient.clear();
        localStorage.removeItem('x-sl-signin-user-email');
        // The `sl-auth-version` cookie is cleared server-side by
        // /api/auth/v2/logout (AuthenticationHelper.RemoveAuthCookies).
        // Belt-and-suspenders: also nuke the legacy `sl-auth-method` flag
        // so any browser that still has the localStorage value from a
        // pre-marker session doesn't hold a stale opinion after logout.
        try { localStorage.removeItem('sl-auth-method'); } catch { /* ignore */ }
        clearDivisions();
        clearAuthFlowData();
      } catch (e) {
        console.warn('SignOut: local cleanup failed:', e);
      }

      window.location.href = redirectUrl;
    };

    performLogout();
  }, [dispatch]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '16px',
        fontSize: '18px',
        fontFamily: 'Geist, sans-serif'
      }}
    >
      <Loading />
      Signing out...
    </div>
  );
};

export default SignOut;
